import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { copyToClipboard, extractRequestId } from '../helpers';

/**
 * Sağlayıcı hata metni, KIRPILMADAN gösterilir.
 *
 * Trendyol GO destek ekibi her hata için "talep no" (requestId) ister; adapter
 * bu numarayı hata mesajının sonuna ekler. Metin kısaltılırsa numara kaybolur
 * ve kullanıcı destek kaydı açamaz — bu yüzden tam metin + tek tıkla kopyalama.
 */

interface CopyRequestIdButtonProps {
  requestId: string;
  size?: 'small' | 'medium';
}

/** "Talep no"yu panoya kopyalar; kopyalandığında kısa süre tik gösterir. */
export const CopyRequestIdButton = ({
  requestId,
  size = 'small'
}: CopyRequestIdButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (await copyToClipboard(requestId)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Tooltip title={copied ? 'Kopyalandı' : `Talep no kopyala: ${requestId}`}>
      <IconButton size={size} onClick={handleCopy} color="inherit">
        {copied ? (
          <CheckIcon fontSize="inherit" />
        ) : (
          <ContentCopyIcon fontSize="inherit" />
        )}
      </IconButton>
    </Tooltip>
  );
};

interface IntegrationErrorNoteProps {
  message?: string | null;
  at?: string | null;
  /** Ardışık başarısız yoklama sayacı (0 ise gösterilmez). */
  pollFailures?: number;
}

/**
 * Entegrasyon listesindeki hata notu. Entegrasyon henüz ERROR'a düşmemiş olsa
 * bile (örn. 5 ardışık hatanın 2.'si) son hata görünür kalır — sorun eşiğe
 * ulaşmadan fark edilsin diye.
 */
const IntegrationErrorNote = ({
  message,
  at,
  pollFailures = 0
}: IntegrationErrorNoteProps) => {
  if (!message) return null;
  const requestId = extractRequestId(message);

  return (
    <Box sx={{ mt: 0.5, maxWidth: 420 }}>
      <Typography
        variant="caption"
        color="error"
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {message}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        {at && (
          <Typography variant="caption" color="text.secondary">
            {new Date(at).toLocaleString('tr-TR')}
          </Typography>
        )}
        {pollFailures > 0 && (
          <Typography variant="caption" color="warning.main">
            · {pollFailures} ardışık başarısız yoklama
          </Typography>
        )}
        {requestId && <CopyRequestIdButton requestId={requestId} />}
      </Stack>
    </Box>
  );
};

interface ProviderErrorAlertProps {
  message: string;
  severity?: 'error' | 'warning';
  onClose?: () => void;
}

/**
 * Doğrula/sağlık sonucu için uyarı kutusu. Snackbar yerine sayfa içinde durur:
 * sağlayıcı hataları uzundur ve 5 saniyede kaybolan bir toast'ta okunamaz.
 */
export const ProviderErrorAlert = ({
  message,
  severity = 'error',
  onClose
}: ProviderErrorAlertProps) => {
  const requestId = extractRequestId(message);
  return (
    <Alert
      severity={severity}
      onClose={onClose}
      sx={{ mb: 2, '& .MuiAlert-message': { overflow: 'hidden' } }}
      action={
        requestId ? (
          <Button
            size="small"
            color="inherit"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={() => void copyToClipboard(requestId)}
          >
            Talep no
          </Button>
        ) : undefined
      }
    >
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {message}
      </Typography>
    </Alert>
  );
};

export default IntegrationErrorNote;
